/**
 * Pricing API Client
 * Handles all API calls to the ecommerce pricing endpoints
 */

import { getHttpClient, i18n } from '@ufc/core'
import type { PricedProduct, PricingProfile } from '../../domain/types'
import type { ProductsQueryParams } from '../../api/api-types'
import {
  validateProductsResponse,
  validateProductResponse,
  validateProfileResponse,
} from '../../api/api-types'
import {
  transformProductsDto,
  transformProductDto,
  transformPricingProfileDto,
  transformFiltersToQuery,
} from '../../api/transformers'
import { PRICING_ENDPOINTS } from '../../domain/constants'

export const pricingApi = {
  /**
   * Get user's pricing profile (requires authentication)
   * Returns tenant, customer group, and price list context
   */
  async getProfile(): Promise<PricingProfile> {
    const http = getHttpClient()
    const response = await http.get<any>(`/api${PRICING_ENDPOINTS.PROFILE}`)
    const validated = validateProfileResponse(response.data)
    return transformPricingProfileDto(validated.data)
  },

  /**
   * Get products with prices
   * Uses default ecommerce prices for guests, user-specific prices for authenticated users
   */
  async getProducts(params?: ProductsQueryParams): Promise<PricedProduct[]> {
    const http = getHttpClient()
    const mergedParams: ProductsQueryParams = {
      ...params,
      locale: params?.locale ?? i18n.language ?? 'en',
    }
    const query = transformFiltersToQuery(mergedParams)
    const queryString = query.toString()
    const endpoint = queryString
      ? `/api${PRICING_ENDPOINTS.PORTAL_PRODUCTS}?${queryString}`
      : `/api${PRICING_ENDPOINTS.PORTAL_PRODUCTS}`

    const response = await http.get<any>(endpoint)
    const validated = validateProductsResponse(response.data)
    return transformProductsDto(validated.data)
  },

  /**
   * Get single product with price
   */
  async getProduct(documentId: string): Promise<PricedProduct | null> {
    const http = getHttpClient()
    const lang = i18n.language || 'en'
    const endpoint = `/api${PRICING_ENDPOINTS.PORTAL_PRODUCT_BY_ID(documentId)}?locale=${encodeURIComponent(lang)}`

    try {
      const response = await http.get<any>(endpoint)
      const validated = validateProductResponse(response.data)
      return transformProductDto(validated.data)
    } catch (error) {
      // Return null for 404
      if ((error as any)?.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  /**
   * Get products by category
   */
  async getProductsByCategory(categoryCode: string): Promise<PricedProduct[]> {
    return pricingApi.getProducts({ categoryCode })
  },

  /**
   * Search products
   */
  async searchProducts(search: string): Promise<PricedProduct[]> {
    return pricingApi.getProducts({ search })
  },
}
